import { useState, useRef, useEffect, RefObject } from 'react';
import { useIntersection } from 'react-use';
import { useTranslations } from 'next-intl';
import { analytics } from '@silvertours/front-entities';
import { InfoBanner as InfoBannerType } from '@silvertours/back-domain-page';
import Close from '@engineering/icons/close';
import { Ui } from '@silvertours/front-shared';
import { Wrapper, CloseButton } from './InfoBanner.styles';

// The second animation frame starts after the first paint. A hidden document
// does not run animation frames, thus a timer is used as a fallback.
const runAfterNextPaint = (callback: () => void) => {
  let hasRun = false;

  const run = () => {
    if (hasRun) return;
    hasRun = true;
    document.removeEventListener('visibilitychange', onVisibilityChange);
    callback();
  };

  const onVisibilityChange = () => {
    if (document.visibilityState === 'hidden') {
      setTimeout(run, 0);
    }
  };

  if (document.visibilityState === 'hidden') {
    setTimeout(run, 0);
    return;
  }

  document.addEventListener('visibilitychange', onVisibilityChange);
  requestAnimationFrame(() => requestAnimationFrame(run));
};

const InfoBanner = ({ closable, sticky, theme, content }: InfoBannerType) => {
  const t = useTranslations('features.navigationLegacy.infoBanner');
  const [isInfoBannerVisible, setIsInfoBannerVisible] = useState(true);

  const intersectionRef = useRef<HTMLElement | null>(null);
  const intersection = useIntersection(
    intersectionRef as RefObject<HTMLElement>,
    {
      root: null,
      rootMargin: '0px',
      threshold: 1,
    },
  );

  useEffect(() => {
    const banner = document.getElementById('infobanner');
    const links = banner?.getElementsByTagName('a');
    if (links) {
      const linkList = [].slice.call(links);
      linkList.forEach(link => {
        const anchor = link as HTMLAnchorElement;
        const label = anchor.pathname.replace(/(^\/)|(\/$)/g, '');
        anchor.onclick = () => analytics.gtm.trackInfoBannerLinkClicked(label);
      });
    }
  }, []);

  const onClose = () => {
    setIsInfoBannerVisible(false);
    // Track after the next paint. A synchronous dataLayer push runs GTM tags
    // in the click task and increases INP.
    runAfterNextPaint(() => {
      analytics.gtm.trackInfoBannerCloseClicked();
      analytics.generic.trackInfoBannerCloseClicked();
    });
  };

  return (
    <aside id="infobanner" ref={intersectionRef}>
      {isInfoBannerVisible && (
        <Wrapper
          isIntersecting={
            sticky &&
            Boolean(intersection && intersection.intersectionRatio < 1)
          }
          className={theme}
        >
          <Ui.RichText content={content} />
          {closable && (
            <CloseButton
              icon={Close}
              onClick={onClose}
              aria-label={t('hideLabel')}
            />
          )}
        </Wrapper>
      )}
    </aside>
  );
};
export { InfoBanner };
